import {Canvas, useFrame, useLoader, useThree} from "@react-three/fiber";
import * as TWEEN from "@tweenjs/tween.js";
import _ from "lodash";
import {CreateAccessor, observer_mgl, RunInAction} from "mobx-graphlink";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Row} from "react-vcomponents";
import * as THREE from "three";
import {XR, XROrigin, createXRStore, useXR} from "@react-three/xr"
import {GLTF, GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js";
import {makeObservable, observable, makeAutoObservable} from "mobx";
import {Timer, WaitXThenRun} from "js-vextensions";
import {VideoTexture_Fixed} from "./VideoTexture_Fixed.js";
import {store} from "../../Store/index.js";

/** The standard useGLTF from @react-three/drei causes the useMemo in the parent component to re-memoize for some reason! (breaking logic that relies on `useMemo(X, [])` from only running once) So use this one instead. */
export const useGLTF_fixed = (path: string|n)=>{
	const [gltf, setGltf] = useState<GLTF|n>(null);
	useMemo(()=>{ if (path != null) new GLTFLoader().load(path, setGltf); }, [path]);
	//return gltf;
	return {
		animations: gltf?.animations,
		scene: gltf?.scene,
		scenes: gltf?.scenes,
		cameras: gltf?.cameras,
		asset: gltf?.asset,
		parser: gltf?.parser,
		userData: gltf?.userData,
	};
};

export const useGLTF_fixed_sharedLoad = (path: string)=>{
	const gltf_shared = LoadGLTF_Fixed_Accessor(path);
	const gltf_clone = useMemo(()=>{
		return {
			animations: (gltf_shared?.animations ?? []).map(a=>a.clone()),
			scene: gltf_shared?.scene?.clone(),
			scenes: (gltf_shared?.scenes ?? []).map(a=>a.clone()),
			cameras: (gltf_shared?.cameras ?? []).map(a=>a.clone()),
			asset: gltf_shared?.asset, //.clone(),
			parser: gltf_shared?.parser, //.map(a=>a.clone()),
			userData: gltf_shared?.userData, //.map(a=>a.clone()),
		};
	}, [gltf_shared?.animations, gltf_shared?.asset, gltf_shared?.cameras, gltf_shared?.parser, gltf_shared?.scene, gltf_shared?.scenes, gltf_shared?.userData]);
	return gltf_clone;
};

// this variant doesn't quite work right atm, for some reason (in tested case: the alarm models are visible, but only during some throws it seems, and the banks are not visible at any point)
export const LoadGLTF_Fixed_Accessor_containers = new Map<string, {gltf: GLTF|n}>();
/** The standard useGLTF from @react-three/drei causes the useMemo in the parent component to re-memoize for some reason! (breaking logic that relies on `useMemo(X, [])` from only running once) So use this one instead. */
export const LoadGLTF_Fixed_Accessor = CreateAccessor((path: string)=>{
	if (!LoadGLTF_Fixed_Accessor_containers.has(path)) {
		const newContainer = makeAutoObservable({gltf: null as GLTF|n});
		LoadGLTF_Fixed_Accessor_containers.set(path, newContainer);
		new GLTFLoader().load(path, gltf=>{
			RunInAction("LoadGLTF_Fixed_Accessor.GLTFLoader.onLoad", ()=>newContainer.gltf = gltf);
		});
	}
	const {gltf} = LoadGLTF_Fixed_Accessor_containers.get(path)!;
	return {
		animations: gltf?.animations,
		scene: gltf?.scene,
		scenes: gltf?.scenes,
		cameras: gltf?.cameras,
		asset: gltf?.asset,
		parser: gltf?.parser,
		userData: gltf?.userData,
	};
});

export function GetCameraWorldPos(camera: THREE.Camera, forceMatrixUpdate = true): THREE.Vector3 {
	if (forceMatrixUpdate) camera.updateMatrixWorld(true); // ensure the camera's world matrix is up-to-date
	const pos = new THREE.Vector3();
	camera.getWorldPosition(pos);
	return pos;
}

export function useSceneClickWithRaycastHandler(opt: {
	handler: (intersects: THREE.Intersection<THREE.Object3D<THREE.Object3DEventMap>>[], event: MouseEvent)=>any,
	renderer: THREE.WebGLRenderer,
	camera: THREE.Camera,
	scene: THREE.Scene,
}) {
	// Create a raycaster for mouse interaction
	const raycaster = useMemo(()=>new THREE.Raycaster(), []);
	const mouse = useMemo(()=>new THREE.Vector2(), []);

	// Click handler function
	const handleClick = useCallback((event: MouseEvent)=>{
		// Calculate mouse position in normalized device coordinates (-1 to +1)
		const rect = opt.renderer.domElement.getBoundingClientRect();
		mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		// Update the raycaster with the camera and mouse position
		raycaster.setFromCamera(mouse, opt.camera);

		// Find intersections
		const intersects = raycaster.intersectObjects(opt.scene.children, true);

		return opt.handler(intersects, event);
	}, [opt.renderer, opt.camera, opt.scene]);

	// Set up the event listener
	useEffect(()=>{
		const domElement = opt.renderer.domElement;
		domElement.addEventListener("click", handleClick);
		return ()=>{
			domElement.removeEventListener("click", handleClick);
		};
	}, [handleClick, opt.renderer]);
}

// old version; seems to not cause the parent to re-render, once the texture is loaded
/*export function TextureDataUriTo3JTexture(textureDataUri: string | n): THREE.Texture | n {
	if (textureDataUri == null) return null;
	const image = new Image();
	image.src = textureDataUri;
	const texture = new THREE.Texture(image);
	texture.needsUpdate = true;
	texture.wrapS = THREE.RepeatWrapping;
	texture.wrapT = THREE.RepeatWrapping;
	texture.repeat.set(1, 1);
	return texture;
}*/

// Utility function to create a texture from base64 synchronously
export function CreateTextureFromBase64Str(base64Str: string, imageWhileLoading?: HTMLImageElement|n, onLoad?: ((img: HTMLImageElement)=>any)|n, texWrap?: boolean): THREE.Texture;
export function CreateTextureFromBase64Str(base64Str: string|n, imageWhileLoading?: HTMLImageElement|n, onLoad?: ((img: HTMLImageElement)=>any)|n, texWrap?: boolean): THREE.Texture|null;
export function CreateTextureFromBase64Str(base64Str: string|n, imageWhileLoading?: HTMLImageElement|n, onLoad?: ((img: HTMLImageElement)=>any)|n, texWrap = true): THREE.Texture|null {
	if (base64Str == null) return null;
	const img = new Image();
	img.src = base64Str;
	const texture = new THREE.Texture(imageWhileLoading ?? img);

	// attempted fix for black-textures issue; didn't help
	// "Without SRGBColorSpace, some mobile GPU drivers produce black output due to color-space mismatch with the renderer's default SRGBColorSpace output."
	//texture.colorSpace = THREE.SRGBColorSpace;

	// If mipmaps are disabled (user setting), use LinearFilter and disable mipmap generation.
	// (Some mobile GPU drivers silently fail to generate mipmaps, causing all textured meshes to show as black in most situations.)
	if (!store.main.settings.allowTextureMipmaps) {
		texture.minFilter = THREE.LinearFilter;
		texture.generateMipmaps = false;
	}

	if (texWrap) {
		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		texture.repeat.set(1, 1);
	}
	img.onload = ()=>{
		// attempted fix for black-textures issue on a certain phone; didn't fix it (but yes, some texs are NPOT atm)
		/*const isPOT = (n: number)=>(n & (n - 1)) === 0 && n > 0;
		if (!isPOT(img.naturalWidth) || !isPOT(img.naturalHeight)) {
			console.warn(`CreateTextureFromBase64Str: NPOT texture rejected (${img.naturalWidth}x${img.naturalHeight}). Use power-of-two dimensions.`, base64Str?.slice(0, 80));
			return; // leave texture with imageWhileLoading image, or blank
		}*/

		texture.image = img;
		texture.needsUpdate = true;
		if (onLoad) onLoad(img);

		// Check for GL errors on next frame (errors are queued, not immediate)
		/*requestAnimationFrame(()=>{
			const gl = document.querySelector("canvas")?.getContext("webgl2") ?? document.querySelector("canvas")?.getContext("webgl");
			const err = gl?.getError();
			if (err && err !== 0) console.warn(`WebGL error after texture upload: 0x${err.toString(16)}`, base64Str?.slice(0, 50));
		});*/
	};
	return texture;
}
/*export const useBase64ImageStrAsTexture_nullTillLoaded = (imageStr: string)=>{
	const [texture, setTexture] = useState<THREE.Texture|null>(null);
	useEffect(()=>{
		const newTexture = CreateTextureFromBase64Str(imageStr);
		setTexture(newTexture);
		return ()=>{
			if (newTexture) newTexture.dispose();
		};
	}, [imageStr]);
	return texture;
};*/

// this one seems to handle parent re-rendering properly (fixing issue of "texture not loading, sometimes")
export const useBase64ImageStrAsTexture = ((imageStr: string|n, imageWhileLoading?: HTMLImageElement|n, onLoad?: ((img: HTMLImageElement)=>any)|n, texWrap = true)=>{
	const texture = useMemo(()=>{
		if (imageStr == tinyTransparentImageAsBase64Str) return GetTinyTransparentImageTexture(); // optimization: if the image is the tiny transparent one, just use the texture already cached for that image-str
		return CreateTextureFromBase64Str(imageStr, imageWhileLoading, onLoad, texWrap);
	}, [imageStr, imageWhileLoading, onLoad, texWrap]);
	useEffect(()=>{
		return ()=>texture?.dispose();
	}, [texture]);
	return texture;
}) as {
	(imageStr: string, imageWhileLoading?: HTMLImageElement, onLoad?: (img: HTMLImageElement)=>any, texWrap?: boolean): THREE.Texture;
	(imageStr: string|n, imageWhileLoading?: HTMLImageElement, onLoad?: (img: HTMLImageElement)=>any, texWrap?: boolean): THREE.Texture|null;
};

export const tinyTransparentImageAsBase64Str = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
// this returns a tiny transparent image-texture (which is instantly available [after the first call], and stays in memory permanently]; use this everywhere you need a transparent texture
export const GetTinyTransparentImageTexture = CreateAccessor(()=>CreateTextureFromBase64Str(tinyTransparentImageAsBase64Str));
export const GetTinyTransparentImage = CreateAccessor(()=>GetTinyTransparentImageTexture().image);
//GetTinyTransparentImage(); // call once at startup, to ensure the texture is ready to go instantly for first use

export const useVideoElementAsTexture = (videoElement: HTMLVideoElement|n)=>{
	const texture = useMemo(()=>{
		if (videoElement == null) return null;
		//const videoTexture = new THREE.VideoTexture(videoElement);
		const videoTexture = new VideoTexture_Fixed(videoElement); // fixes memory leak (see: https://github.com/mrdoob/three.js/pull/31375)

		// fixes that VideoTexture otherwise shows up as "too bright", the first time the given video-clip is displayed (see: https://discourse.threejs.org/t/r3f-meshbasicmaterial-too-bright-after-replacing-texture/44434/5)
		// NOTE: When using this in react-three-fiber, you must also set the `onUpdate` prop of the material to `a=>a.needsUpdate = true`, otherwise this color-space change will not take effect in every case.
		videoTexture.colorSpace = THREE.SRGBColorSpace;

		return videoTexture;
	}, [videoElement]);
	useEffect(()=>{
		return ()=>texture?.dispose();
	}, [texture]);
	return texture;
}

/*export const useVideoElementAsTexture_withCanvasResizing = (videoElement: HTMLVideoElement|n, targetWidth: number, targetHeight: number)=>{
	const texture = useMemo(()=>{
		if (videoElement == null) return null;

		// Create canvas for downscaling
		const canvas = document.createElement("canvas");
		canvas.width = targetWidth;
		canvas.height = targetHeight;
		const ctx = canvas.getContext("2d")!;

		// Create texture from canvas
		const texture = new THREE.CanvasTexture(canvas);
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.format = THREE.RGBAFormat;

		// Update function to render video to canvas at lower resolution
		function updateTexture() {
			if (videoElement == null) return;
			if (videoElement.readyState >= videoElement.HAVE_CURRENT_DATA) {
				ctx.drawImage(videoElement, 0, 0, targetWidth, targetHeight);
				texture.needsUpdate = true;
			}
		}

		// Auto-update when video plays
		let lastTime = videoElement.currentTime;
		const timer = new Timer(1000 / 15, ()=>{
			if (videoElement.currentTime != lastTime) {
				lastTime = videoElement.currentTime;
				updateTexture();
			} else if (videoElement.currentTime >= 4.9) {
				timer.Stop();
			}
		}).Start();

		return {texture, timer};
	}, [videoElement, targetWidth, targetHeight]);

	useEffect(()=>{
		return ()=>{
			if (texture) {
				texture.timer.Stop();
				texture.texture.dispose();
			}
		};
	}, [texture]);

	return texture?.texture ?? null;
};*/